import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  buildToastSuccess,
  defaultValue,
  Form,
  IS_DEMO,
  LoadingIndicator,
  postApproveNewStep2,
  Response,
  ResponsePostCto,
  translate,
  CtoStepEnum,
  userSelector,
  useFeatureFlag,
} from "@shape/reef-shared";
import type { NewStep2ApprovalForm, StepProps } from "@shape/reef-shared";
import {
  postCreateNewCtoDecision,
  postCreateNewStep2Mdb,
  postCreateNewStep2RiskAssessmentComplement,
} from "services/cto";
import { type CreateStep2MdbFormValues, type Step2Response } from "types";
import { isCountryAllowedForSisModificationAssessment } from "utils/vesselsRules";
import { ApprovalDecisionGate } from "./components/ApprovalDecisionGate";
import { ProcessSafetyComplement } from "./components/ProcessSafetyComplement";
import { RejectedReviewDetails } from "./components/RejectedReviewDetails";
import { SmeAssessmentReview } from "./components/SmeAssessmentReview";
import {
  buildStep2RiskAssessmentComplementBody,
  buildStep2SmeAssessmentBody,
} from "./helpers/step2RemasterForm";
import { useNewStep2EvaluationState } from "./helpers/useNewStep2EvaluationState";

const getStep2RemasterFormDefaultValues = (
  initialValues: Step2Response | null | undefined,
  readOnly: boolean
): CreateStep2MdbFormValues => {
  const defaultFormValues = (initialValues as unknown as CreateStep2MdbFormValues) || {
    draft: false,
  };
  const assigningRecommendations = defaultFormValues.assigning_recommendations?.map(
    (recommendation, index) => ({
      ...recommendation,
      tags: recommendation.tags ?? initialValues?.assigning_recommendations?.[index]?.tag_codes,
    })
  );
  const normalizedFormValues = {
    ...defaultFormValues,
    assigning_recommendations: assigningRecommendations,
  };

  if (readOnly || normalizedFormValues.assigning_recommendations?.length) {
    return normalizedFormValues;
  }

  return {
    ...normalizedFormValues,
    assigning_recommendations: [{}] as CreateStep2MdbFormValues["assigning_recommendations"],
  };
};

const getStep2DisplayInitialValues = (initialValues: Step2Response): Step2Response => ({
  ...initialValues,
  assigning_recommendations: initialValues.assigning_recommendations.map((recommendation) => ({
    ...recommendation,
    tags: recommendation.tags ?? recommendation.tag_codes,
  })),
});

export const NewStep2EvaluationRemaster: FC<StepProps<Step2Response>> = ({
  vesselId,
  ctoId,
  cto,
  readOnly,
  ctoStepResponse,
  isCurrentVersion,
  currentStep,
  onConclusionDateLoaded,
}: StepProps<Step2Response>) => {
  const initialValues = ctoStepResponse.data;
  const { country = "" } = useParams<{ country?: string }>();
  const flaggedQuestions = useFeatureFlag("flagged-questions");
  const isSisModificationAssessmentEnabled = useFeatureFlag("cto-sis-modification-assessment");
  const isSisModificationEnabledForContext =
    isSisModificationAssessmentEnabled &&
    isCountryAllowedForSisModificationAssessment(country, IS_DEMO);
  const [createdStep2, setCreatedStep2] = useState<Response<ResponsePostCto>>(defaultValue);
  const formDefaultValues = getStep2RemasterFormDefaultValues(initialValues, readOnly);

  const form = useForm<CreateStep2MdbFormValues>({
    mode: "onChange",
    defaultValues: formDefaultValues,
  });
  const approvalForm = useForm<NewStep2ApprovalForm>({
    mode: "onChange",
    defaultValues: {
      approved: null,
      comments: "",
    },
  });
  const approvals = form.watch("approvals") ?? [];
  const approvalFormIsApproved = approvalForm.watch("approved");
  const loggedUser = useSelector(userSelector);
  const {
    approvalButtonLabelKey,
    isApprovalRejected,
    isProcessSafetyReviewRejected,
    needsStep1Approval,
    processSafetyApprovalButtonLabelKey,
    processSafetyComplementReadOnly,
    riskAssessmentDecisions,
    shouldShowProcessSafetyComplement,
    shouldShowProcessSafetyReview,
    shouldShowStep2Assessment,
    step1ApprovalRejected,
    step2AssessmentReadOnly,
  } = useNewStep2EvaluationState({
    approvalFormIsApproved,
    approvals,
    initialValues,
    isCurrentVersion,
    loggedUserId: loggedUser?.user_id,
    readOnly,
  });
  const approvalButtonText = translate(approvalButtonLabelKey);
  const processSafetyApprovalButtonText = translate(processSafetyApprovalButtonLabelKey);
  const hasSisModification = form.watch("has_sis_modification");

  const fieldsArrayControl = useFieldArray({
    control: form.control,
    name: "assigning_recommendations",
  });
  const { replace: replaceAssigningRecommendations } = fieldsArrayControl;

  useEffect(() => {
    if (initialValues) {
      const nextFormValues = getStep2RemasterFormDefaultValues(initialValues, readOnly);
      form.reset(nextFormValues);
      replaceAssigningRecommendations(nextFormValues.assigning_recommendations);
    }
  }, [form, initialValues, readOnly, replaceAssigningRecommendations]);

  const onUploadSuccess = () => {
    toast.success(translate("genericComponents.file.uploadFileSuccess"), {
      closeButton: true,
      draggable: false,
    });
  };

  const submitStep2SmeAssessment = useCallback(
    (values: CreateStep2MdbFormValues) => {
      postCreateNewStep2Mdb(vesselId, ctoId, buildStep2SmeAssessmentBody(values), setCreatedStep2);
    },
    [ctoId, vesselId]
  );

  const submitStep2RiskAssessmentComplement = useCallback(
    (values: CreateStep2MdbFormValues) => {
      postCreateNewStep2RiskAssessmentComplement(
        vesselId,
        ctoId,
        buildStep2RiskAssessmentComplementBody(values),
        setCreatedStep2
      );
    },
    [ctoId, vesselId]
  );

  const submitStep2 = useCallback(
    (values: CreateStep2MdbFormValues) => {
      if (shouldShowProcessSafetyComplement) {
        submitStep2RiskAssessmentComplement(values);
        return;
      }

      submitStep2SmeAssessment(values);
    },
    [
      shouldShowProcessSafetyComplement,
      submitStep2RiskAssessmentComplement,
      submitStep2SmeAssessment,
    ]
  );

  const handleDraftSubmit = useCallback(() => {
    submitStep2(form.getValues());
  }, [form, submitStep2]);

  const handleValidSubmit = useCallback(
    (values: CreateStep2MdbFormValues) => {
      submitStep2({ ...values, draft: false });
    },
    [submitStep2]
  );

  const handleApprove = useCallback(
    (data: NewStep2ApprovalForm) => {
      postApproveNewStep2(vesselId, ctoId, data, setCreatedStep2);
    },
    [ctoId, vesselId]
  );

  const handleProcessSafetyApprove = useCallback(
    (data: NewStep2ApprovalForm) => {
      postCreateNewCtoDecision(
        vesselId,
        ctoId,
        {
          decision_type: "RISK_ASSESSMENT_REVIEW",
          decision: data.approved === true ? 1 : 0,
          ...(data.approved === false ? { justification: data.comments } : {}),
        },
        setCreatedStep2
      );
    },
    [ctoId, vesselId]
  );

  const validateRejectedApprovalComments = useCallback(
    (comments?: string) => approvalFormIsApproved !== false || !!comments?.trim(),
    [approvalFormIsApproved]
  );

  const validateSisModificationAssessment = useCallback(() => {
    if (!isSisModificationEnabledForContext || hasSisModification !== true) return true;

    const sisJustification = form.getValues("sis_modification_justification")?.trim();
    const sisFiles = form.getValues("sis_modification_justification_files") ?? [];

    return !!sisJustification || sisFiles.length > 0;
  }, [form, hasSisModification, isSisModificationEnabledForContext]);

  useEffect(() => {
    buildToastSuccess(createdStep2.data?.cto_code);
  }, [createdStep2]);

  useEffect(() => {
    if (approvalFormIsApproved) approvalForm.setValue("comments", "");
  }, [approvalFormIsApproved, approvalForm]);

  useEffect(() => {
    if (initialValues?.step_approved_at && currentStep && currentStep > CtoStepEnum.STEP_2) {
      onConclusionDateLoaded?.({
        date: initialValues.step_approved_at,
        name: initialValues.step_approved_by?.name ?? "-",
      });
    }
  }, [
    currentStep,
    initialValues?.step_approved_at,
    initialValues?.step_approved_by?.name,
    onConclusionDateLoaded,
  ]);

  if (ctoStepResponse.loading || !initialValues) return <LoadingIndicator />;

  const displayInitialValues = getStep2DisplayInitialValues(initialValues);
  const submitDisabled = !!createdStep2.data?.cto_code;
  const submitButtonClassName = (() => {
    let classNames = "approval-button ";
    if (approvalFormIsApproved) classNames += "isApproved";
    return classNames;
  })();

  return (
    <Form formData={form} onValidSubmit={handleValidSubmit}>
      {step1ApprovalRejected && (
        <RejectedReviewDetails
          title={translate("formGeneric.rejectedBy")}
          userName={approvals[0]?.user?.name}
          comments={approvals[0]?.comments}
        />
      )}

      {!step1ApprovalRejected && needsStep1Approval && (
        <ApprovalDecisionGate
          approvalButtonText={approvalButtonText}
          approvalForm={approvalForm}
          approvalFormIsApproved={approvalFormIsApproved}
          createdStep2={createdStep2}
          isApprovalRejected={isApprovalRejected}
          label={translate("ctoStep2.approveInfoTitle")}
          onSubmit={handleApprove}
          rejectLabel={translate("ctoStep2.moreInformationRequired")}
          submitButtonClassName={submitButtonClassName}
          submitDisabled={submitDisabled}
          validateRejectedApprovalComments={validateRejectedApprovalComments}
        />
      )}

      {shouldShowStep2Assessment && (
        <>
          <SmeAssessmentReview
            createdStep2={createdStep2}
            cto={cto}
            ctoId={ctoId}
            fieldsArrayControl={fieldsArrayControl}
            form={form}
            handleDraftSubmit={handleDraftSubmit}
            hasSisModification={hasSisModification}
            initialValues={displayInitialValues}
            isCurrentVersion={isCurrentVersion}
            isSisModificationEnabledForContext={isSisModificationEnabledForContext}
            onUploadSuccess={onUploadSuccess}
            readOnly={step2AssessmentReadOnly}
            rejectedReviewDetails={
              !isCurrentVersion &&
              !step1ApprovalRejected &&
              !needsStep1Approval &&
              isProcessSafetyReviewRejected ? (
                <RejectedReviewDetails
                  title={translate("ctoStep2.processSafetyMoreInformationRequired")}
                  userName={riskAssessmentDecisions[0]?.user?.name}
                  comments={riskAssessmentDecisions[0]?.justification}
                />
              ) : null
            }
            showActions={!step2AssessmentReadOnly}
            showRecommendations={!shouldShowProcessSafetyComplement}
            submitDisabled={submitDisabled}
            validateSisModificationAssessment={validateSisModificationAssessment}
            vesselId={vesselId}
            flaggedQuestions={flaggedQuestions}
          />

          {shouldShowProcessSafetyReview && (
            <ApprovalDecisionGate
              approvalButtonText={processSafetyApprovalButtonText}
              approvalForm={approvalForm}
              approvalFormIsApproved={approvalFormIsApproved}
              createdStep2={createdStep2}
              isApprovalRejected={isApprovalRejected}
              label={translate("ctoStep2.processSafetyApproveInfoTitle")}
              onSubmit={handleProcessSafetyApprove}
              rejectLabel={translate("ctoStep2.moreInformationRequired")}
              submitButtonClassName={submitButtonClassName}
              submitDisabled={submitDisabled}
              validateRejectedApprovalComments={validateRejectedApprovalComments}
            />
          )}
        </>
      )}

      {shouldShowProcessSafetyComplement && (
        <ProcessSafetyComplement
          cto={cto}
          ctoId={ctoId}
          createdStep2={createdStep2}
          fieldsArrayControl={fieldsArrayControl}
          form={form}
          handleDraftSubmit={handleDraftSubmit}
          initialValues={displayInitialValues}
          isCurrentVersion={isCurrentVersion}
          onUploadSuccess={onUploadSuccess}
          readOnly={processSafetyComplementReadOnly}
          submitDisabled={submitDisabled}
          vesselId={vesselId}
          flaggedQuestions={flaggedQuestions}
        />
      )}
    </Form>
  );
};
